import { NekoPosSdk } from 'neko-common';
import { configs } from '../configs';
import authProvider from '../authProvider';

// Re-export the interfaces from the SDK
export type {
  OrdersResponse,
  OrdersFilter,
  OrdersSort,
  OrdersParams,
} from 'neko-common';

// Create SDK instance with proper authentication context
const sdk = new NekoPosSdk(configs.apiRoot);

// Set up the auth client for the SDK
sdk.createAuthClient(configs, () => {
  // This will be called when authentication is successful
  console.log('Authentication successful');
});

// Wrapper function to handle 401 errors with token refresh
const withTokenRefresh = async <T>(apiCall: () => Promise<T>): Promise<T> => {
  try {
    return await apiCall();
  } catch (error: any) {
    // Check if it's a 401 error
    if (error?.status === 401 || error?.message?.includes('401')) {
      console.log(
        '401 error detected in ordersService, attempting token refresh...'
      );

      try {
        // Attempt to refresh the token
        await authProvider.auth.fetchRefreshToken();
        console.log('Token refresh successful, retrying API call...');

        // Retry the original API call
        return await apiCall();
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // If refresh fails, throw the original error
        throw error;
      }
    }

    // If it's not a 401 error, throw the original error
    throw error;
  }
};

// Create a wrapper around the order service that handles 401 errors
const createOrdersServiceWithRefresh = () => {
  const originalService = sdk.order;

  return {
    getOrders: async (params: any) => {
      return withTokenRefresh(() => originalService.getOrders(params));
    },
    getOrder: async (id: string) => {
      return withTokenRefresh(() => originalService.getOrder(id));
    },
    createOrder: async (data: any) => {
      return withTokenRefresh(() => originalService.createOrder(data));
    },
    updateOrder: async (id: string, data: any) => {
      return withTokenRefresh(() => originalService.updateOrder(id, data));
    },
    updateOrderStatus: async (id: string, status: any) => {
      return withTokenRefresh(() =>
        originalService.updateOrderStatus(id, status)
      );
    },
    markAsDelivered: async (id: string) => {
      return withTokenRefresh(async () => {
        const authHeader = await (sdk as any).getAuthHeader();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        if (
          authHeader &&
          typeof authHeader === 'object' &&
          'Authorization' in authHeader &&
          authHeader.Authorization
        ) {
          headers.Authorization = authHeader.Authorization;
        }

        const response = await fetch(
          `${configs.apiRoot}/orders/${id}/deliver`,
          {
            method: 'POST',
            headers,
          }
        );

        if (!response.ok) {
          throw new Error(
            `Failed to mark order as delivered: ${response.status} ${response.statusText}`
          );
        }

        return await response.json();
      });
    },
    createSuborder: async (parentOrderId: string) => {
      return withTokenRefresh(async () => {
        const authHeader = await (sdk as any).getAuthHeader();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        if (
          authHeader &&
          typeof authHeader === 'object' &&
          'Authorization' in authHeader &&
          authHeader.Authorization
        ) {
          headers.Authorization = authHeader.Authorization;
        }

        const response = await fetch(
          `${configs.apiRoot}/orders/${parentOrderId}/suborder`,
          {
            method: 'POST',
            headers,
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message ||
              `Failed to create suborder: ${response.status} ${response.statusText}`
          );
        }

        return await response.json();
      });
    },
    getOrdersReport: async (params: { from: string; to: string }) => {
      return withTokenRefresh(async () => {
        console.log(
          'Making API call to orders report endpoint:',
          `/reports/orders_total_by_date?from=${params.from}&to=${params.to}`
        );

        // Get the auth header for the request
        const authHeader = await (sdk as any).getAuthHeader();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        // Add auth header if available
        if (
          authHeader &&
          typeof authHeader === 'object' &&
          'Authorization' in authHeader &&
          authHeader.Authorization
        ) {
          headers.Authorization = authHeader.Authorization;
        }

        // Make the API call directly using fetch
        const response = await fetch(
          `${configs.apiRoot}/reports/orders_total_by_date?from=${params.from}&to=${params.to}`,
          {
            method: 'GET',
            headers,
          }
        );

        if (!response.ok) {
          throw new Error(
            `API call failed: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();
        console.log('Orders report API response:', data);
        return data;
      });
    },
    deleteOrder: async (id: string) => {
      return withTokenRefresh(async () => {
        const authHeader = await (sdk as any).getAuthHeader();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        if (
          authHeader &&
          typeof authHeader === 'object' &&
          'Authorization' in authHeader &&
          authHeader.Authorization
        ) {
          headers.Authorization = authHeader.Authorization;
        }

        const response = await fetch(`${configs.apiRoot}/orders/${id}`, {
          method: 'DELETE',
          headers,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message ||
              `Failed to delete order: ${response.status} ${response.statusText}`
          );
        }

        return await response.json();
      });
    },
  };
};

// Export the wrapped service
export const ordersService = createOrdersServiceWithRefresh();
